import React, { useState, useEffect } from "react";
import { s } from "../lib/theme";
import { fmt, holidayOn, LEAVE_TYPES, businessDays, HOURS_PER_DAY } from "../lib/pto";
import { StatusPill } from "./Shell";
import AttachmentList from "./AttachmentList";
import { Requests, errMsg } from "../lib/api";

// ------- Request row: view + edit + attachments -------
export function ReqRow({ r, admin, employeeBalance, employees, onApprove, onDeny, refreshAll }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  async function act(fn) {
    setBusy(true);
    try { await fn(r, comment); }
    finally { setBusy(false); }
  }

  async function doDelete() {
    setBusy(true);
    try {
      await Requests.remove(r.id);
      if (refreshAll) await refreshAll();
    } catch (e) {
      alert(errMsg(e, "Could not delete this entry."));
      setBusy(false);
      setConfirmDel(false);
    }
  }

  if (editing) {
    return (
      <EditRow
        r={r}
        currentBalance={employeeBalance}
        employees={employees || []}
        onCancel={() => setEditing(false)}
        onSaved={async () => {
          setEditing(false);
          if (refreshAll) await refreshAll();
        }}
      />
    );
  }

  return (
    <div style={s.reqRow} data-testid={`req-row-${r.id}`}>
      <div style={{ flex: 1, minWidth: 240 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {admin && <strong style={{ fontSize: 14 }}>{r.user_name}</strong>}
          <span style={{ fontSize: 14, color: "#374151" }}>{r.type_label}</span>
          <StatusPill status={r.status} />
          {r.late_flag && <span style={s.lateTag}>Short notice</span>}
        </div>
        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 3 }}>
          {fmt(r.start)}{r.end !== r.start ? ` – ${fmt(r.end)}` : ""} · {r.business_days} day{r.business_days === 1 ? "" : "s"}
          {r.hours > 0 ? ` · ${r.hours} hrs PTO` : " · no PTO used"}
        </div>
        {r.note && (
          <div style={{ fontSize: 13, color: "#4b5563", marginTop: 4, fontStyle: "italic" }}>
            &ldquo;{r.note}&rdquo;
          </div>
        )}
        {r.admin_comment && (
          <div style={{ fontSize: 13, color: "#1f2937", marginTop: 4 }}>
            <strong>Dr. Kirk:</strong> {r.admin_comment}
          </div>
        )}
        {r.status === "approved" && (
          <div style={s.readyTag}>✓ Approved — ready to place on practice schedule</div>
        )}
        <AttachmentList
          requestId={r.id}
          attachments={r.attachments || []}
          canManage={admin || r.status === "pending"}
          canRemove={admin || r.status === "pending"}
          onChanged={refreshAll}
        />
      </div>
      {admin && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 220 }}>
          {r.status === "pending" && (
            <>
              <input
                data-testid={`req-comment-${r.id}`}
                style={{ ...s.input, margin: 0, fontSize: 13 }}
                placeholder="Comment (optional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <div style={{ display: "flex", gap: 6 }}>
                <button data-testid={`req-approve-${r.id}`} disabled={busy} style={s.btnApprove} onClick={() => act(onApprove)}>Approve</button>
                <button data-testid={`req-deny-${r.id}`} disabled={busy} style={s.btnDeny} onClick={() => act(onDeny)}>Deny</button>
              </div>
            </>
          )}
          <div style={{ display: "flex", gap: 6 }}>
            <button
              data-testid={`req-edit-${r.id}`}
              style={{ ...s.btnGhost, padding: "6px 10px", fontSize: 12, flex: 1 }}
              onClick={() => setEditing(true)}
            >
              ✎ Edit
            </button>
            <button
              data-testid={`req-delete-${r.id}`}
              disabled={busy}
              style={{ ...s.btnGhost, padding: "6px 10px", fontSize: 12, color: "#b91c1c", borderColor: "#fca5a5", flex: 1 }}
              onClick={() => setConfirmDel(true)}
            >
              🗑 Delete
            </button>
          </div>
          {confirmDel && (
            <div data-testid={`req-delete-confirm-${r.id}`} style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: 10, fontSize: 12, color: "#7f1d1d" }}>
              Delete this entry permanently?
              {r.status === "approved" && Number(r.hours) > 0 && (
                <div style={{ marginTop: 3 }}>{r.hours} hrs will be refunded to {r.user_name.split(" ")[0]}.</div>
              )}
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                <button data-testid={`req-delete-yes-${r.id}`} disabled={busy} style={{ ...s.btnDeny, flex: 1 }} onClick={doDelete}>
                  {busy ? "Deleting…" : "Yes, delete"}
                </button>
                <button data-testid={`req-delete-no-${r.id}`} disabled={busy} style={{ ...s.btnGhost, padding: "6px 10px", fontSize: 12 }} onClick={() => setConfirmDel(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ------- Inline edit form -------
function EditRow({ r, currentBalance, employees, onCancel, onSaved }) {
  const [userId, setUserId] = useState(r.user_id);
  const [type, setType] = useState(r.type);
  const [start, setStart] = useState(r.start);
  const [end, setEnd] = useState(r.end);
  // Hours field: string so user can clear it to trigger recompute
  const [hoursStr, setHoursStr] = useState(String(r.hours ?? ""));
  const [note, setNote] = useState(r.note || "");
  const [adminComment, setAdminComment] = useState(r.admin_comment || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const meta = LEAVE_TYPES[type];
  const bd = businessDays(start, end);
  const dateOrTypeChanged = type !== r.type || start !== r.start || end !== r.end;
  const employeeChanged = userId !== r.user_id;

  const emps = (employees || []).filter((u) => u.role !== "admin" && u.active !== false);
  const newEmp = emps.find((u) => u.id === userId);
  const oldEmp = emps.find((u) => u.id === r.user_id);

  // Effective hours if user leaves the override blank
  let effectiveHours;
  if (!meta.deducts) {
    effectiveHours = 0;
  } else if (hoursStr.trim() === "") {
    effectiveHours = dateOrTypeChanged ? bd * HOURS_PER_DAY : Number(r.hours || 0);
  } else {
    effectiveHours = Number(hoursStr);
  }

  const oldHours = Number(r.hours || 0);

  // Balance impact preview
  let balanceLine = null;
  if (r.status === "approved") {
    if (employeeChanged) {
      const oldBal = oldEmp ? Number(oldEmp.balance_hours || 0) : 0;
      const newBal = newEmp ? Number(newEmp.balance_hours || 0) : 0;
      const oldAfter = Math.max(0, oldBal + oldHours);
      const newAfter = Math.max(0, newBal - effectiveHours);
      balanceLine = (
        <span style={{ color: "#92400e" }}>
          · {oldEmp?.name?.split(" ")[0] || "old"} balance {oldBal} → {oldAfter} (+{oldHours} refund)
          · {newEmp?.name?.split(" ")[0] || "new"} balance {newBal} → {newAfter} (-{effectiveHours})
        </span>
      );
    } else {
      const bal = typeof currentBalance === "number" ? currentBalance : 0;
      const delta = oldHours - effectiveHours;
      const projected = Math.max(0, bal + delta);
      balanceLine = (
        <span style={{ color: delta === 0 ? "#6b7280" : (delta > 0 ? "#166534" : "#92400e") }}>
          · balance {bal} {delta === 0 ? "unchanged" : `→ ${projected}`}
          {delta > 0 && ` (+${delta})`}
          {delta < 0 && ` (${delta})`}
        </span>
      );
    }
  }

  async function save() {
    setErr("");
    if (!start || !end || bd === 0) { setErr("Pick a valid weekday range."); return; }
    if (hoursStr.trim() !== "" && (isNaN(Number(hoursStr)) || Number(hoursStr) < 0)) {
      setErr("Hours must be zero or greater.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        user_id: userId,
        type,
        start,
        end,
        note,
        admin_comment: adminComment,
      };
      if (hoursStr.trim() !== "") payload.hours = Number(hoursStr);
      await Requests.update(r.id, payload);
      await onSaved();
    } catch (e) {
      setErr(errMsg(e, "Could not save edit."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ ...s.reqRow, background: "#f8fafc", padding: 14, borderRadius: 8, flexDirection: "column", alignItems: "stretch" }} data-testid={`edit-form-${r.id}`}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#14304a", marginBottom: 8 }}>
        Editing: {r.user_name}
        {employeeChanged && newEmp && (
          <span style={{ color: "#b91c1c", marginLeft: 8 }}>→ {newEmp.name}</span>
        )}
      </div>
      {emps.length > 0 && (
        <>
          <label style={s.label}>Employee</label>
          <select data-testid={`edit-user-${r.id}`} style={s.input} value={userId} onChange={(e) => setUserId(e.target.value)}>
            {emps.map((u) => (
              <option key={u.id} value={u.id}>{u.name} — {u.balance_hours} hrs balance</option>
            ))}
          </select>
        </>
      )}
      <div style={s.row2}>
        <div>
          <label style={s.label}>Type</label>
          <select data-testid={`edit-type-${r.id}`} style={s.input} value={type} onChange={(e) => setType(e.target.value)}>
            {Object.entries(LEAVE_TYPES).map(([k, v]) => (
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={s.label}>Hours (blank = {bd * HOURS_PER_DAY} hrs from dates)</label>
          <input
            data-testid={`edit-hours-${r.id}`}
            style={s.input}
            type="number"
            step="0.25"
            min="0"
            value={hoursStr}
            onChange={(e) => setHoursStr(e.target.value)}
            placeholder={meta.deducts ? String(bd * HOURS_PER_DAY) : "N/A"}
            disabled={!meta.deducts}
          />
        </div>
      </div>
      <div style={s.row2}>
        <div>
          <label style={s.label}>Start</label>
          <input data-testid={`edit-start-${r.id}`} style={s.input} type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>
        <div>
          <label style={s.label}>End</label>
          <input data-testid={`edit-end-${r.id}`} style={s.input} type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
      </div>
      <label style={s.label}>Employee note</label>
      <textarea data-testid={`edit-note-${r.id}`} style={{ ...s.input, height: 50, resize: "vertical" }} value={note} onChange={(e) => setNote(e.target.value)} />
      <label style={s.label}>Admin comment</label>
      <textarea data-testid={`edit-admin-comment-${r.id}`} style={{ ...s.input, height: 50, resize: "vertical" }} value={adminComment} onChange={(e) => setAdminComment(e.target.value)} />

      <div style={s.calcRow}>
        <span>{bd} weekday{bd === 1 ? "" : "s"}</span>
        <span>· old {oldHours} hrs → new <strong>{effectiveHours}</strong> hrs</span>
        {balanceLine}
      </div>

      {err && <div style={s.error}>{err}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button data-testid={`edit-save-${r.id}`} disabled={busy} style={{ ...s.btnPrimary, flex: 1, marginTop: 0 }} onClick={save}>
          {busy ? "Saving…" : "Save changes"}
        </button>
        <button data-testid={`edit-cancel-${r.id}`} disabled={busy} style={{ ...s.btnGhost, padding: "10px 16px" }} onClick={onCancel}>Cancel</button>
      </div>

      <AttachmentList
        requestId={r.id}
        attachments={r.attachments || []}
        canManage
        canRemove
        onChanged={onSaved}
      />
    </div>
  );
}

// ------- Month calendar (unchanged from before) -------
export function MonthCalendar({ requests, users, scope, onMonthChange }) {
  const [cursor, setCursor] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  });

  useEffect(() => {
    if (onMonthChange) onMonthChange(cursor);
  }, [cursor, onMonthChange]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const monthName = cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const palette = ["#0f5132", "#14304a", "#7c2d12", "#5b21b6", "#9d174d", "#115e59", "#854d0e"];
  const employees = (users || []).filter((u) => u.role !== "admin" && u.active !== false);
  const colorFor = (userId) => {
    const idx = employees.findIndex((u) => u.id === userId);
    return palette[(idx < 0 ? 0 : idx) % palette.length];
  };

  function entriesOn(date) {
    const ds = date.toISOString().slice(0, 10);
    return requests.filter((r) => ds >= r.start && ds <= r.end && r.status !== "denied");
  }

  const statusStyle = (r) => {
    if (scope === "all") {
      const base = colorFor(r.user_id);
      return r.status === "pending"
        ? { background: "#fff", color: base, border: `1px dashed ${base}` }
        : { background: base, color: "#fff", border: `1px solid ${base}` };
    }
    return r.status === "approved"
      ? { background: "#0f5132", color: "#fff" }
      : { background: "#fff7ed", color: "#9a3412", border: "1px solid #fed7aa" };
  };

  return (
    <div>
      <div style={s.calHeader}>
        <button data-testid="cal-prev-btn" className="no-print" style={s.calNav} onClick={() => setCursor(new Date(year, month - 1, 1))}>‹ Prev</button>
        <div data-testid="cal-month-label" style={s.calMonth}>{monthName}</div>
        <button data-testid="cal-next-btn" className="no-print" style={s.calNav} onClick={() => setCursor(new Date(year, month + 1, 1))}>Next ›</button>
      </div>

      <div style={s.calGrid}>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} style={s.calDow}>{d}</div>
        ))}
        {cells.map((date, i) => {
          if (!date) return <div key={i} style={{ ...s.calCell, background: "#fafafa" }} />;
          const dow = date.getDay();
          const weekend = dow === 0 || dow === 6;
          const items = entriesOn(date);
          const isToday = date.toDateString() === new Date().toDateString();
          const hol = holidayOn(date);
          return (
            <div
              key={i}
              style={{
                ...s.calCell,
                ...(weekend ? { background: "#f6f7f6" } : {}),
                ...(hol ? { background: "#fef9ec" } : {}),
                ...(isToday ? { boxShadow: "inset 0 0 0 2px #14304a" } : {}),
              }}
            >
              <div style={s.calNum}>{date.getDate()}</div>
              {hol && (
                <div style={s.calHoliday} title={`${hol.name} — office closed, paid holiday`}>
                  ★ {hol.name}
                </div>
              )}
              {items.slice(0, 4).map((r) => (
                <div key={r.id} style={{ ...s.calBar, ...statusStyle(r) }} title={`${r.user_name} · ${r.type_label} · ${r.status}`}>
                  {scope === "all" ? r.user_name.split(" ")[0] : r.type_label.replace("PTO — ", "")}
                  {r.status === "pending" ? " (pending)" : ""}
                </div>
              ))}
              {items.length > 4 && <div style={s.calMore}>+{items.length - 4} more</div>}
            </div>
          );
        })}
      </div>

      {scope === "all" && (
        <div style={s.legend}>
          {employees.map((u) => (
            <span key={u.id} style={s.legendItem}>
              <span style={{ ...s.legendDot, background: colorFor(u.id) }} />
              {u.name.split(" ")[0]}
            </span>
          ))}
          <span style={s.legendItem}>
            <span style={{ ...s.legendDot, background: "#fff", border: "1px dashed #888" }} />
            dashed = pending
          </span>
          <span style={s.legendItem}>
            <span style={{ ...s.legendDot, background: "#fde68a" }} />
            ★ paid holiday (office closed)
          </span>
        </div>
      )}
    </div>
  );
}

// ------- PTO Policy (unchanged) -------
export function PolicyTab() {
  return (
    <div style={s.card} data-testid="policy-card">
      <div style={s.cardTitle}>Paid Time Off (PTO) Policy</div>
      <div style={{ fontSize: 14, lineHeight: 1.65, color: "#374151" }}>
        <p style={s.pp}>
          Our PTO plan combines vacation, sick, and personal days into one paid time-off
          allowance. How you use your hours is your choice. Holidays are provided separately
          and do not come out of your PTO.
        </p>

        <div style={s.polHead}>How much you receive</div>
        <p style={s.pp}>
          Your annual PTO is based on your length of service, granted in two halves each year:
          one half on January 2 and one half on July 1.
        </p>
        <table style={{ ...s.table, marginBottom: 14 }}>
          <thead>
            <tr><th style={s.th}>Years of service</th><th style={s.th}>Annual PTO</th></tr>
          </thead>
          <tbody>
            <tr><td style={s.td}>Under 3 months</td><td style={s.td}>0 hours</td></tr>
            <tr><td style={s.td}>3 months – 1 year</td><td style={s.td}>Prorated</td></tr>
            <tr><td style={s.td}>1 – 3 years</td><td style={s.td}>80 hours (≈ 2 weeks)</td></tr>
            <tr><td style={s.td}>Over 3 years</td><td style={s.td}>120 hours (≈ 3 weeks)</td></tr>
            <tr><td style={s.td}>Over 6 years</td><td style={s.td}>160 hours (≈ 4 weeks)</td></tr>
          </tbody>
        </table>
        <p style={s.pp}>
          8 hours equals 1 day. New employees begin accruing on their first day but may not
          use PTO until they have completed 90 days of continuous employment. At the 3-year
          anniversary, the additional 40 hours is prorated for the remainder of that year.
        </p>

        <div style={s.polHead}>Requesting time off</div>
        <p style={s.pp}>
          Submit PTO requests at least <strong>three weeks (21 days) in advance</strong>.
          Requests made with shorter notice may be denied. Dr. Kirk may deny a request if an
          overriding business need takes precedence, and will otherwise make every effort to
          accommodate requests made with enough notice.
        </p>
        <p style={s.pp}>
          Do not place your own time off on the practice schedule. Once a request is approved,
          it will be placed on the schedule for you.
        </p>

        <div style={s.polHead}>Carryover and payout</div>
        <p style={s.pp}>
          Unused PTO may carry over up to the cap for your service level, but no more than one
          year. Carryover caps: 24 hours under 1 year, 32 hours at 1–3 years, 40 hours at over
          3 years, and 48 hours at over 6 years.
        </p>
        <p style={s.pp}>
          PTO is not paid out on an involuntary termination, and is not paid out if a voluntary
          resignation notice is less than 10 working days. Otherwise, payout caps run 40 hours
          at 1–3 years, 80 hours at over 3 years, and 120 hours at over 6 years.
        </p>

        <div style={s.polNote}>
          This is a plain-language summary for quick reference. The Tennessee Implant &amp; Oral
          Surgery Policy Manual is the official policy. If anything here differs from the manual,
          the manual governs. See Dr. Kirk with questions.
        </div>
      </div>
    </div>
  );
}
